import { Text } from "@dropbox/dig-components/typography";
import type {
  FssOpsToolCall,
  FssOpsToolCallCopyFiles,
  FssOpsToolCallCreateFolder,
  FssOpsToolCallDeleteFiles,
  FssOpsToolCallMoveFiles,
  FssOpsToolCallRenameFile,
} from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/fss/types";
import type { ToolCallRowLayoutProps } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/ToolCallRowLayout";
import { ToolCallRowLayout } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/ToolCallRowLayout";
import type { ToolCallProgressStatus } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/types";
import i18n from "@mirage/translations";
import { memo } from "react";

interface FssOpsToolCallRowProps {
  toolCall: FssOpsToolCall;
  status: ToolCallProgressStatus | undefined;
}
export const FssOpsToolCallRow = memo(
  ({ toolCall, status }: FssOpsToolCallRowProps) => {
    switch (toolCall.name) {
      case "create_folder":
        return <CreateFolderToolCallRow toolCall={toolCall} status={status} />;
      case "move_files":
        return <MoveFilesToolCallRow toolCall={toolCall} status={status} />;
      case "rename_file":
        return <RenameFileToolCallRow toolCall={toolCall} status={status} />;
      case "copy_files":
        return <CopyFilesToolCallRow toolCall={toolCall} status={status} />;
      case "delete_files":
        return <DeleteFilesToolCallRow toolCall={toolCall} status={status} />;
      default:
        toolCall satisfies never;
        throw new Error(`Unknown tool call: ${toolCall}`);
    }
  },
);
FssOpsToolCallRow.displayName = "FssOpsToolCallRow";

interface FssOpsToolCallRowGenericProps<T> {
  toolCall: T;
  status: ToolCallProgressStatus | undefined;
}

type CreateFolderToolCallRowProps =
  FssOpsToolCallRowGenericProps<FssOpsToolCallCreateFolder>;
export const CreateFolderToolCallRow = memo(
  ({ toolCall, status }: CreateFolderToolCallRowProps) => {
    const fields: ToolCallRowLayoutProps["fields"] = [
      {
        label: i18n.t("action_label_create_folder_field_path"),
        value: toolCall.arguments.folder_path,
      },
    ];
    if (toolCall.arguments.rationale) {
      fields.push({
        label: i18n.t("action_label_field_rationale"),
        value: toolCall.arguments.rationale,
      });
    }
    return (
      <ToolCallRowLayout
        actionLabel={i18n.t("action_label_create_folder")}
        fields={fields}
        status={status}
      />
    );
  },
);
CreateFolderToolCallRow.displayName = "CreateFolderToolCallRow";

type MoveFilesToolCallRowProps =
  FssOpsToolCallRowGenericProps<FssOpsToolCallMoveFiles>;
export const MoveFilesToolCallRow = memo(
  ({ toolCall, status }: MoveFilesToolCallRowProps) => {
    const fields: ToolCallRowLayoutProps["fields"] = [
      {
        label: i18n.t("action_label_move_files_field_items"),
        value: (
          <FssOpsToolCallFileList filePaths={toolCall.arguments.source_paths} />
        ),
      },
      {
        label: i18n.t("action_label_move_files_field_to"),
        value: toolCall.arguments.destination_folder_path,
      },
    ];
    if (toolCall.arguments.rationale) {
      fields.push({
        label: i18n.t("action_label_field_rationale"),
        value: toolCall.arguments.rationale,
      });
    }
    return (
      <ToolCallRowLayout
        actionLabel={i18n.t("action_label_move_files")}
        fields={fields}
        status={status}
      />
    );
  },
);
MoveFilesToolCallRow.displayName = "MoveFilesToolCallRow";

type RenameFileToolCallRowProps =
  FssOpsToolCallRowGenericProps<FssOpsToolCallRenameFile>;
export const RenameFileToolCallRow = memo(
  ({ toolCall, status }: RenameFileToolCallRowProps) => {
    const fields: ToolCallRowLayoutProps["fields"] = [
      {
        label: i18n.t("action_label_rename_file_field_from"),
        value: toolCall.arguments.source_path,
      },
      {
        label: i18n.t("action_label_rename_file_field_to"),
        value: toolCall.arguments.new_path,
      },
    ];
    if (toolCall.arguments.rationale) {
      fields.push({
        label: i18n.t("action_label_field_rationale"),
        value: toolCall.arguments.rationale,
      });
    }
    return (
      <ToolCallRowLayout
        actionLabel={i18n.t("action_label_rename_file")}
        fields={fields}
        status={status}
      />
    );
  },
);
RenameFileToolCallRow.displayName = "RenameFileToolCallRow";

type CopyFilesToolCallRowProps =
  FssOpsToolCallRowGenericProps<FssOpsToolCallCopyFiles>;
export const CopyFilesToolCallRow = memo(
  ({ toolCall, status }: CopyFilesToolCallRowProps) => {
    const fields: ToolCallRowLayoutProps["fields"] = [
      {
        label: i18n.t("action_label_copy_files_field_items"),
        value: (
          <FssOpsToolCallFileList filePaths={toolCall.arguments.source_paths} />
        ),
      },
      {
        label: i18n.t("action_label_copy_files_field_to"),
        value: toolCall.arguments.destination_folder_path,
      },
    ];
    if (toolCall.arguments.rationale) {
      fields.push({
        label: i18n.t("action_label_field_rationale"),
        value: toolCall.arguments.rationale,
      });
    }
    return (
      <ToolCallRowLayout
        actionLabel={i18n.t("action_label_copy_files")}
        fields={fields}
        status={status}
      />
    );
  },
);
CopyFilesToolCallRow.displayName = "CopyFilesToolCallRow";

type DeleteFilesToolCallRowProps =
  FssOpsToolCallRowGenericProps<FssOpsToolCallDeleteFiles>;
export const DeleteFilesToolCallRow = memo(
  ({ toolCall, status }: DeleteFilesToolCallRowProps) => {
    const fields: ToolCallRowLayoutProps["fields"] = [
      {
        label: i18n.t("action_label_delete_files_field_items"),
        value: (
          <FssOpsToolCallFileList filePaths={toolCall.arguments.file_paths} />
        ),
      },
    ];
    if (toolCall.arguments.rationale) {
      fields.push({
        label: i18n.t("action_label_field_rationale"),
        value: toolCall.arguments.rationale,
      });
    }
    return (
      <ToolCallRowLayout
        actionLabel={i18n.t("action_label_delete_files")}
        fields={fields}
        status={status}
      />
    );
  },
);
DeleteFilesToolCallRow.displayName = "DeleteFilesToolCallRow";

interface FssOpsToolCallFileListProps {
  filePaths: string[];
}
export const FssOpsToolCallFileList = memo(
  ({ filePaths }: FssOpsToolCallFileListProps) => {
    return (
      <div>
        {filePaths.map((filePath, index) => (
          <Text key={index} tagName="div">
            {filePath}
          </Text>
        ))}
      </div>
    );
  },
);
FssOpsToolCallFileList.displayName = "FssOpsToolCallFileList";
